import { i18n } from '../shared/i18n'
import { setAuthToken } from './api'
import { refreshUserToken } from './auth-service'
import { getStoredRefreshToken, storeRefreshedTokens, storeTokenExpiresAt } from './auth-storage'
import { logger } from './logger'

interface QueuedRequest {
  resolve: (token: string) => void
  reject: (error: Error) => void
}

class RefreshTokenService {
  private isRefreshing = false
  private requestQueue: QueuedRequest[] = []

  async refresh(): Promise<string | null> {
    if (this.isRefreshing) {
      return this.enqueueRequest()
    }

    this.isRefreshing = true

    try {
      const refreshToken = getStoredRefreshToken()

      if (!refreshToken) {
        throw new Error(i18n.t('common.errors.noRefreshToken'))
      }

      const response = await refreshUserToken(refreshToken)

      storeRefreshedTokens(response.accessToken, response.refreshToken)
      storeTokenExpiresAt(response.expiresIn)
      setAuthToken(response.accessToken)

      logger.info('[RefreshTokenService] Token refreshed successfully')
      this.processQueue(response.accessToken)

      return response.accessToken
    } catch (error_) {
      const err = error_ instanceof Error ? error_ : new Error(String(error_))

      logger.error('[RefreshTokenService] Token refresh failed:', error_)
      this.processQueue(null, err)

      return null
    } finally {
      this.isRefreshing = false
    }
  }

  private enqueueRequest(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.requestQueue.push({ resolve, reject })
    })
  }

  private processQueue(token: string | null, error?: Error): void {
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift()!

      if (error) {
        request.reject(error)
      } else {
        request.resolve(token!)
      }
    }
  }

  /**
   * Called when the session is torn down (refresh endpoint 401 / refresh failed). A refresh that
   * is still in flight would otherwise find an empty queue and leave callers that were waiting on
   * it pending forever, so reject them here — same as the mobile app's `refreshTokenService`.
   */
  reset(): void {
    const pendingQueue = this.requestQueue
    this.isRefreshing = false
    this.requestQueue = []
    for (const request of pendingQueue) {
      request.reject(new Error('Auth session reset'))
    }
  }
}

export const refreshTokenService = new RefreshTokenService()
